import type { EditableBadgeOption } from '@chatsdet/ui';
import type { AgentTaskStatus } from './types';

export const STATUS_VARIANTS: Record<AgentTaskStatus, 'default' | 'success' | 'warning' | 'destructive' | 'outline'> = {
  waiting: 'outline',
  actionable: 'warning',
  in_progress: 'default',
  completed: 'success',
  canceled: 'destructive',
  expired: 'destructive',
};

export const STATUS_LABELS: Record<AgentTaskStatus, string> = {
  waiting: 'Waiting',
  actionable: 'Actionable',
  in_progress: 'In Progress',
  completed: 'Completed',
  canceled: 'Canceled',
  expired: 'Expired',
};

export const PRIORITY_LABELS = ['Normal', 'High', 'Critical'];

/** Options for status EditableBadge dropdown */
export const STATUS_OPTIONS: EditableBadgeOption<AgentTaskStatus>[] = [
  { id: 'waiting', label: 'Waiting' },
  { id: 'actionable', label: 'Actionable' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'canceled', label: 'Canceled' },
  { id: 'expired', label: 'Expired' },
];

/** Options for priority EditableBadge dropdown */
export const PRIORITY_OPTIONS: EditableBadgeOption<string>[] = [
  { id: '0', label: 'Normal' },
  { id: '1', label: 'High' },
  { id: '2', label: 'Critical' },
];

/** Kanban board column definitions (excludes expired — auto-system state) */
export const KANBAN_COLUMNS: { id: AgentTaskStatus; label: string }[] = [
  { id: 'waiting', label: 'Waiting' },
  { id: 'actionable', label: 'Actionable' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'canceled', label: 'Canceled' },
];

/** Accent colors for kanban column top borders */
export const COLUMN_COLORS: Record<string, string> = {
  waiting: 'border-t-zinc-400',
  actionable: 'border-t-amber-500',
  in_progress: 'border-t-blue-500',
  completed: 'border-t-emerald-500',
  canceled: 'border-t-red-500',
};

/** Background tints for column drop targets */
export const COLUMN_DROP_COLORS: Record<string, string> = {
  waiting: 'bg-zinc-500/5',
  actionable: 'bg-amber-500/5',
  in_progress: 'bg-blue-500/5',
  completed: 'bg-emerald-500/5',
  canceled: 'bg-red-500/5',
};

/** Valid status transitions for drag-and-drop */
export const VALID_TRANSITIONS: Record<AgentTaskStatus, AgentTaskStatus[]> = {
  waiting: ['actionable'],
  actionable: ['in_progress', 'completed', 'canceled'],
  in_progress: ['actionable', 'waiting', 'completed', 'canceled'],
  completed: ['actionable', 'in_progress'],
  canceled: ['actionable', 'in_progress'],
  expired: [],
};

/** Maps (source, target) status pair to the API action */
export const TRANSITION_ACTIONS: Record<string, 'trigger' | 'claim' | 'complete' | 'cancel' | 'reopen' | 'release'> = {
  'waiting->actionable': 'trigger',
  'actionable->in_progress': 'claim',
  'actionable->completed': 'complete',
  'actionable->canceled': 'cancel',
  'in_progress->actionable': 'release',
  'in_progress->waiting': 'release',
  'in_progress->completed': 'complete',
  'in_progress->canceled': 'cancel',
  'completed->actionable': 'reopen',
  'completed->in_progress': 'reopen',
  'canceled->actionable': 'reopen',
  'canceled->in_progress': 'reopen',
};
